import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Target,
  ShowerHead,
  WashingMachine,
  Zap,
  Wifi,
  Scissors,
  Shirt,
  Stethoscope,
  BookOpen,
} from "lucide-react";
import { Reveal } from "./Reveal";

const HOSTEL_IMG =
  "https://customer-assets-agu9un31.emergentagent.net/job_health-ed-platform/artifacts/3434cc8a2c1e5dc3_Modern%20Compound%20Under%20Night%20Lights.png";

const AMENITIES = [
  { icon: ShieldCheck, t: "24×7 Security & Safety" },
  { icon: Target, t: "Recreational & Indoor Activities — Carrom, Chess & Table Tennis" },
  { icon: ShowerHead, t: "Clean & Hygienic Bathrooms" },
  { icon: WashingMachine, t: "Utility Area — Washing Machine Facility" },
  { icon: Zap, t: "24×7 Power Backup" },
  { icon: Wifi, t: "Wi-Fi Services" },
  { icon: Scissors, t: "Salon Services on Call" },
  { icon: Shirt, t: "Laundry Services on Call" },
  { icon: Stethoscope, t: "Doctor & Medical Services on Call" },
  { icon: BookOpen, t: "Library & Reading Room Access Till 8 PM" },
];

export default function HostelLife() {
  return (
    <section className="bg-navy py-24 lg:py-32 relative overflow-hidden" data-testid="hostel-section">
      <div className="grain-overlay" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-10 items-center mb-14">
          <Reveal>
            <div>
              <span className="text-xs tracking-[0.3em] uppercase text-gold font-mono">Why Choose SVS</span>
              <h2 className="font-serif text-4xl sm:text-5xl text-sand mt-4 leading-tight">
                Hostel <span className="italic text-gold-gradient">life</span>
              </h2>
              <p className="text-sand/60 mt-5 leading-relaxed">
                A safe, comfortable &amp; supportive home away from home.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <motion.div
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.3 }}
              className="relative overflow-hidden rounded-[2px] ring-1 ring-gold/30 shadow-[0_20px_60px_rgba(0,0,0,0.35)]"
              data-testid="hostel-image"
            >
              <img
                src={HOSTEL_IMG}
                alt="SVS Hostel building illuminated at night"
                loading="lazy"
                className="w-full aspect-[16/9] object-cover"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-gold/20" />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-navy-dark/90 to-transparent p-5">
                <span className="text-gold text-xs tracking-widest uppercase font-mono">SVS Hostel</span>
              </div>
            </motion.div>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
          {AMENITIES.map((a, i) => (
            <Reveal key={a.t} delay={i * 0.05}>
              <motion.div
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
                className="h-full bg-navy-dark ring-1 ring-gold/20 rounded-[2px] p-6 flex flex-col items-start gap-4"
                data-testid={`hostel-amenity-${i}`}
              >
                <div className="h-11 w-11 rounded-full bg-gold/15 ring-1 ring-gold/40 flex items-center justify-center shrink-0">
                  <a.icon size={20} className="text-gold" />
                </div>
                <p className="text-sand/85 text-sm font-medium leading-snug">{a.t}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14 text-center" data-testid="hostel-tagline">
            <div className="font-serif text-2xl sm:text-3xl text-gold-gradient tracking-wide">
              Safe. Comfortable. Disciplined. Connected.
            </div>
            <p className="mt-4 text-sand/60 max-w-2xl mx-auto leading-relaxed">
              More than a hostel — a supportive second home where students can learn, relax and grow.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
